import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Navigate,
  Route,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import NetlifyIdentityContext, { useIdentityContext } from 'react-netlify-identity-gotrue';
import { get } from 'lodash';
import moment from 'moment';
import {
  useMediaQuery, useTheme, Box, Button, Typography,
} from '@mui/material';
import CacheBuster from 'react-cache-buster';
import { Widget } from '@typeform/embed-react';
import { useQuery, useMutation } from '@apollo/client';
import packageInfo from '../package.json';
import { CREATE_TOKEN_SIGN_IN, GET_IS_AUTHENTICATED, GENERATE_MEDICAL_RECORDS } from './queries';
import AdminContainer from './components/Admin';
import Courses from './components/Courses';
import CreatePassword from './components/CreatePassword';
import Dashboard from './components/Dashboard';
import FeedbackContainer from './components/Feedback';
import Engagement from './components/Engagement';
import ForgotPassword from './components/Forgot';
import ForgotPasswordSubmission from './components/ForgotSubmission';
import Login from './components/Login';
import Profiles from './components/Profiles';
import AdminPhotosContainer from './components/Photos';
import PetPhotos from './components/Photos/PetPhotos';
import ScrollToTop from './components/ScrollToTop';
import ErrorPage from './components/ErrorPage';
import WithSideNav from './components/withSideNav';
import { beforeCapture } from './utilities';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import QRCode from './components/QRCode';
import { useAwoContext } from './components/AwoContextProvider';
import { track } from './components/Hub/analytics';
import Cases from './components/Cases';
import Organizations from './components/Organizations';
import SingleOrganization from './components/Organizations/Single';
import SettingsPanel from './components/Organizations/SettingsPanel';
import MembersPanel from './components/Organizations/MembersPanel';
import Settings from './components/Settings';
import SettingsContent from './components/Settings/SettingsContent';
import MembersContent from './components/Settings/MembersContent';
import ReportsContent from './components/Settings/ReporstContent';

import SMSContainer from './components/SMS';
import TagReviewContainer from './components/TagReview';
import HubContainer from './components/Hub';
import Home from './components/Hub/Home';
import Messages from './components/Hub/Messages';
import MyPets from './components/Hub/MyPets';
import HubSettings from './components/Hub/Settings';
import Typeform from './components/Hub/Typeform';
import HubLogin from './components/Hub/Login';
import Donate from './components/Hub/Donate';
import CommunityMoments from './components/Hub/CommunityMoments';
import MyPhotos from './components/Hub/MyPhotos';
import LessonsLanding from './components/Hub/Lessons/LessonsLanding';
import LessonDetail from './components/Hub/Lessons/LessonDetail';
import PublicLessonPage from './components/PublicLesson/PublicLessonPage';
import RetiredGuideRedirect from './components/PublicLesson/RetiredGuideRedirect';
import { listLessons } from './components/Lesson';
import Products from './components/Hub/Products';
import Albums from './components/Hub/Albums';
import AlbumsRedirect from './components/Hub/Albums/AlbumsRedirect';
import AlbumDetail from './components/Hub/Albums/AlbumDetail';
import SharedAlbum from './components/Hub/Albums/SharedAlbum';
import { DevSettingsProvider } from './contexts/DevSettingsContext';

// How long to wait for the embed to signal `onReady` before assuming it was blocked.
// Generous on purpose — a slow network shouldn't be mistaken for a blocked iframe.
const INVITE_FORM_READY_TIMEOUT_MS = 7000;

function InvitePage() {
  const { awo, loading } = useAwoContext();
  // `blocked` flips true if the embed never reports ready — the iframe is suppressed by
  // Safari cross-site tracking prevention, an ad blocker, or a network/firewall filter.
  const [blocked, setBlocked] = React.useState(false);
  const readyRef = React.useRef(false);

  const isProduction = window.location.hostname.includes('app.petcademy');
  let widgetTypeformId = isProduction ? 'zOiUOhW6' : 'H0moK7G1';
  if (awo?.preSurrenderDashboard) {
    widgetTypeformId = isProduction ? 'wrbGG2Ke' : 'obn0GjSK';
  }

  React.useEffect(() => {
    if (loading) return undefined;
    // Denominator: every time the invite tab is opened. `invite_form_blocked` / this = the
    // rate at which the embed fails to load, segmentable by `userAgent` to confirm the
    // iOS-Safari correlation.
    track('invite_form_view', { awo: awo.name, typeformId: widgetTypeformId });
    const timer = setTimeout(() => {
      if (!readyRef.current) {
        setBlocked(true);
        track('invite_form_blocked', {
          awo: awo.name,
          typeformId: widgetTypeformId,
          userAgent: navigator.userAgent,
        });
      }
    }, INVITE_FORM_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading, awo.name, widgetTypeformId]);

  if (loading) return null;

  // Top-level form URL used as the fallback. Loading the form as a first-party page (rather
  // than an embedded third-party iframe) sidesteps the cross-site tracking prevention that
  // blocks the embed. The `awo` hidden field is passed via query string, mirroring the embed.
  const fallbackUrl = `https://petcademy.typeform.com/to/${widgetTypeformId}?awo=${encodeURIComponent(awo.name || '')}`;

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Widget
        id={widgetTypeformId}
        style={{
          height: '100%',
          width: '100%',
          margin: '0',
          overflow: 'hidden',
        }}
        domain="petcademy.typeform.com"
        medium="awo-invite"
        hidden={{ awo: awo.name }}
        iframeProps={{ title: 'Invite Pet Parent' }}
        onReady={() => {
          readyRef.current = true;
          setBlocked(false);
          track('invite_form_ready', { awo: awo.name, typeformId: widgetTypeformId });
        }}
      />
      {blocked && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6">The invite form couldn&apos;t load here</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Your browser&apos;s privacy settings may be blocking the embedded form. You can open it
            in a new tab instead.
          </Typography>
          <Button
            variant="contained"
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('invite_form_fallback_click', {
              awo: awo.name,
              typeformId: widgetTypeformId,
            })}
          >
            Open invite form in a new tab
          </Button>
        </Box>
      )}
    </Box>
  );
}

function AuthenticateToken() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  localStorage.setItem('petcademyToken', token);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
    noSsr: true,
  });
  const { loading, data } = useQuery(GET_IS_AUTHENTICATED, {
    variables: {
      token: searchParams.get('token'),
    },
  });
  const [createTokenSignIn] = useMutation(CREATE_TOKEN_SIGN_IN, {
    ignoreResults: true,
  });
  const [generateMedicalRecords] = useMutation(GENERATE_MEDICAL_RECORDS, {
    ignoreResults: true,
  });
  React.useEffect(() => {
    // Strip the magic-link token from the address bar once we've persisted it, so it
    // doesn't linger in browser history / referer headers / Sentry pageloads.
    if (token) {
      const params = new URLSearchParams(window.location.search);
      params.delete('token');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
      );
    }
  }, [token]);
  if (loading) return null;
  if (data && data.isAuthenticated) {
    createTokenSignIn({
      variables: {
        token,
        platform: isDesktop ? 'desktop' : 'mobile',
      },
    });
    generateMedicalRecords();
    const to = searchParams.get('redirect') || '/hub/home';
    return <Navigate to={to} state={{ firstLogIn: true }} />;
  }
  // A lesson link whose token is missing, expired or invalid must land on that
  // lesson's public page, never a generic login screen — these links live in
  // Zendesk macros and old emails long after a 30-day token lapses (QA 5.3).
  const failedRedirect = searchParams.get('redirect') || '';
  if (failedRedirect.startsWith('/hub/lessons-and-guides/')) {
    const slug = failedRedirect.split('?')[0].split('/').filter(Boolean).pop();
    const publicPath = listLessons().find((l) => l.slug === slug)?.publicPath;
    if (publicPath) return <Navigate to={publicPath} replace />;
  }
  return <Navigate to="/" state={{ authError: 'You need to login before continuing.', comingFrom: location.pathname + location.hash }} replace />;
}

function RequireTokenAuth({ children }) {
  const location = useLocation();
  const token = localStorage.getItem('petcademyToken');
  const isProduction = process.env.NODE_ENV === 'production';

  if (isProduction && !token) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/hub/login" state={{ authError: 'You need to login before continuing.', comingFrom: location.pathname + location.hash }} replace />;
  }

  return (
    <CacheBuster
      currentVersion={packageInfo.version}
      isEnabled={isProduction} // If false, the library is disabled.
      isVerboseMode={false} // If true, the library writes verbose logs to console.
      loadingComponent={children}
    >
      {children}
    </CacheBuster>
  );
}

function RequireAuth({ children, roles }) {
  const identity = useIdentityContext();
  const location = useLocation();
  const tokenString = localStorage.getItem('ni.goTrueToken');
  if (tokenString === null) {
    return <Navigate to="/" state={{ authError: 'You need to login before continuing.', comingFrom: location.pathname + location.hash }} replace />;
  }

  const tokenJSON = tokenString ? JSON.parse(tokenString) : { expires_at: 0 };
  const isProduction = process.env.NODE_ENV === 'production';

  const tokenExpired = moment.utc().isAfter(tokenJSON.expires_at);
  if (tokenExpired) {
    // don't do anything - wait for re-render
    return '';
  }

  if (!identity.user) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/" state={{ authError: 'You need to login before continuing.', comingFrom: location.pathname + location.hash }} replace />;
  }

  if (roles && get(identity, 'user.app_metadata.roles[0]') !== roles) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/" state={{ authError: 'This page is for admins only.' }} replace />;
  }

  return (
    <WithSideNav>
      <Sentry.ErrorBoundary
        key={location.pathname}
        fallback={<ErrorPage />}
        beforeCapture={beforeCapture}
      >
        <CacheBuster
          currentVersion={packageInfo.version}
          isEnabled={isProduction} // If false, the library is disabled.
          isVerboseMode={false} // If true, the library writes verbose logs to console.
          loadingComponent={children}
        >
          {children}
        </CacheBuster>
      </Sentry.ErrorBoundary>
    </WithSideNav>
  );
}

function RequireInviteToken({ children }) {
  const location = useLocation();
  if (!(location.hash.includes('invite_token') || location.hash.includes('recovery_token'))) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

function App() {
  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
  return (
    <NetlifyIdentityContext url="https://app.petcademy.org">
      <Router>
        <ScrollToTop />
        <SentryRoutes>
          <Route path="/" element={<Login />} />
          <Route
            path="/create-password"
            element={(
              <RequireInviteToken>
                <CreatePassword />
              </RequireInviteToken>
            )}
          />
          {/* Public lesson pages. Paths mirror Column E of the lessons sheet so
              existing links keep working once learning.petcademy.org points here.
              The category segment is decorative — PublicLessonPage resolves on the
              slug alone — which is why Dog Care Guide's two-segment sheet URL can be
              served by the same component. */}
          <Route path="/lesson/:category/:slug" element={<PublicLessonPage />} />
          {/* Ahead of the generic rule below: the retired Home to Home guides are
              301'd by public/_redirects, but a CDN node holding the pre-deploy
              index.html serves this route instead. See RetiredGuideRedirect. */}
          <Route path="/guide/rehoming/:slug" element={<RetiredGuideRedirect />} />
          <Route path="/guide/:category/:slug" element={<PublicLessonPage />} />
          <Route path="/guide/:slug" element={<PublicLessonPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/albums/share/:petId/:albumId"
            element={(
              <SharedAlbum />
            )}
          />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/forgot-submission" element={<ForgotPasswordSubmission />} />
          <Route
            path="/dashboard"
            element={(
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            )}
          />
          <Route
            path="/authenticate"
            element={(
              <AuthenticateToken />
            )}
          />
          <Route
            path="/hub/login"
            element={(
              <HubLogin />
            )}
          />
          <Route
            path="/hub"
            element={(
              <RequireTokenAuth>
                <DevSettingsProvider>
                  <HubContainer />
                </DevSettingsProvider>
              </RequireTokenAuth>
            )}
          >
            <Route
              path="home"
              element={(
                <Home />
              )}
            />
            <Route
              path="messages"
              element={(
                <Messages />
              )}
            />
            <Route
              path="my-pets"
              element={(
                <MyPets />
              )}
            />
            <Route
              path="lessons-and-guides"
              element={(
                <LessonsLanding />
              )}
            />
            <Route
              path="lessons-and-guides/:slug"
              element={(
                <LessonDetail />
              )}
            />
            <Route
              path="products"
              element={(
                <Products />
              )}
            />
            <Route
              path="settings"
              element={(
                <HubSettings />
              )}
            />
            <Route
              path="dog-adopter-day-10-checkin"
              element={(
                <Typeform />
              )}
            />
            <Route
              path="cat-adopter-day-10-checkin"
              element={(
                <Typeform />
              )}
            />
            <Route
              path="dog-adopter-day-10-survey"
              element={<Navigate to="/hub/dog-adopter-day-10-checkin" replace />}
            />
            <Route
              path="cat-adopter-day-10-survey"
              element={<Navigate to="/hub/cat-adopter-day-10-checkin" replace />}
            />
            <Route
              path="adoption-review"
              element={(
                <Typeform />
              )}
            />
            <Route
              path="petcademy-review"
              element={(
                <Typeform />
              )}
            />
            <Route
              path="donate"
              element={(
                <Donate />
              )}
            />
            <Route
              path="community"
              element={(
                <CommunityMoments />
              )}
            />
            <Route
              path="photos"
              element={(
                <MyPhotos />
              )}
            />
            <Route
              path="products"
              element={(
                <Products />
              )}
            />
            <Route
              path="albums"
              element={(
                <AlbumsRedirect />
              )}
            />
            <Route
              path="albums/all"
              element={(
                <Albums />
              )}
            />
            <Route
              path="albums/:petId/:albumId"
              element={(
                <AlbumDetail />
              )}
            />
            <Route
              path="welcome"
              element={<Navigate to="/hub/home" replace />}
            />
          </Route>
          <Route
            path="/engagement"
            element={(
              <RequireAuth>
                <Engagement />
              </RequireAuth>
            )}
          />
          <Route
            path="/feedback"
            element={(
              <RequireAuth>
                <FeedbackContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/feedback/:tab"
            element={(
              <RequireAuth>
                <FeedbackContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/courses"
            element={(
              <RequireAuth>
                <Courses />
              </RequireAuth>
            )}
          />
          <Route
            path="/profiles"
            element={(
              <RequireAuth>
                <Profiles />
              </RequireAuth>
            )}
          />
          <Route
            path="/photos"
            element={(
              <RequireAuth>
                <PetPhotos />
              </RequireAuth>
            )}
          />
          <Route
            path="/awo-pics"
            element={(
              <RequireAuth roles="admin">
                <AdminPhotosContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/sms"
            element={(
              <RequireAuth roles="admin">
                <SMSContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/typeforms"
            element={(
              <RequireAuth roles="admin">
                <AdminContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/invite"
            element={(
              <RequireAuth>
                <InvitePage />
              </RequireAuth>
            )}
          />
          <Route
            path="/qr-code"
            element={(
              <RequireAuth>
                <QRCode />
              </RequireAuth>
            )}
          />
          <Route
            path="/cases"
            element={(
              <RequireAuth>
                <Cases />
              </RequireAuth>
            )}
          />
          <Route
            path="/organizations"
            element={(
              <RequireAuth roles="admin">
                <Organizations />
              </RequireAuth>
            )}
          />
          <Route
            path="/organizations/:organizationId"
            element={(
              <RequireAuth roles="admin">
                <SingleOrganization />
              </RequireAuth>
            )}
          >
            <Route
              path="settings"
              element={<SettingsPanel />}
            />
            <Route
              path="members"
              element={<MembersPanel />}
            />
          </Route>
          <Route
            path="/settings"
            element={(
              <RequireAuth>
                <Settings />
              </RequireAuth>
            )}
          >
            <Route index element={<SettingsContent />} />
            <Route path="members" element={<MembersContent />} />
            <Route path="reports" element={<ReportsContent />} />
          </Route>
          <Route
            path="/tag-review"
            element={(
              <RequireAuth roles="admin">
                <TagReviewContainer />
              </RequireAuth>
            )}
          />
          <Route
            path="/tag-review/:ticketId"
            element={(
              <RequireAuth roles="admin">
                <TagReviewContainer />
              </RequireAuth>
            )}
          />
        </SentryRoutes>
      </Router>
    </NetlifyIdentityContext>
  );
}

export default App;
